import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import AnalyticsFilterBar from './AnalyticsFilterBar';
import { toQuery, useAnalyticsFilters } from './filters';
import { StackedBar } from './charts';
import { deltaPct, fmtHours, fmtMin, fmtNum, fmtPct } from './format';
import { Kpi, Tile, alert, card, cardHead, grid, muted, td, tdR, th, thR } from './Overview';

// "Straty / Przestoje" (spec 7.5): all lost time in one place — short stops, changeovers
// (derived from item switches), remaining downtime — by reason, by line, longest events.

interface Data {
  kpi: Kpi; previous: Kpi | null;
  byReason: { reason: string; minutes: number; count: number }[];
  byLine: (Kpi & { line: string; line_id: number })[];
  longest: { line: string; start: string; minutes: number; reason: string | null; material: string | null }[];
  changeovers: {
    count: number; minutes: number; avgMinutes: number | null; medianMinutes: number | null;
    list: { line: string; start: string; minutes: number; from_sku: string | null; to_sku: string | null; from_material: string | null; to_material: string | null; from_color: string | null; to_color: string | null }[];
    transitions: { transition: string; count: number; minutes: number; min: number; max: number; avg: number }[];
  };
  manual: { type: string; count: number; minutes: number }[];
  thresholds: { short_stop_seconds: number };
}

const dt = (iso: string) => new Date(iso).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

const HBar: React.FC<{ rows: { label: string; value: number; sub?: string; color?: string }[]; format: (v: number) => string }> = ({ rows, format }) => {
  const max = Math.max(1, ...rows.map(r => r.value));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.length === 0 && <div style={{ ...muted, padding: 12 }}>—</div>}
      {rows.map(r => (
        <div key={r.label} style={{ display: 'grid', gridTemplateColumns: '150px 1fr auto', alignItems: 'center', gap: 10, fontSize: 13 }}>
          <span style={{ fontWeight: 600, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</span>
          <div style={{ height: 10, background: '#f1f5f9', borderRadius: 5, overflow: 'hidden' }}><div style={{ width: `${(r.value / max) * 100}%`, height: '100%', background: r.color ?? '#e8590c' }} /></div>
          <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}><b>{format(r.value)}</b> {r.sub && <span style={muted}>{r.sub}</span>}</span>
        </div>
      ))}
    </div>
  );
};

const Downtime: React.FC = () => {
  const { t } = useLanguage();
  const [filters, setFilters] = useAnalyticsFilters();
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/analytics/downtime', { params: toQuery(filters) })
      .then(res => { if (!cancelled) { setData(res.data); setError(null); } })
      .catch(err => { if (!cancelled) setError(err?.response?.data?.message ?? t('analytics.loadError')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, t]);

  const k = data?.kpi;
  const p = data?.previous ?? null;
  const d = (cur: number | null | undefined, prev: number | null | undefined) => (filters.compare && p ? deltaPct(cur, prev) : null);
  const reasonLabel = (r: string) => (r === 'no_speed' || r === 'filament_lost' ? t(`analytics.reason_${r}`) : r === 'unknown' ? t('downtime.reasonUnknown') : r);
  const lossMin = k ? k.ssMin + k.otMin : 0;

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>⛔ {t('downtime.title')}</h1>
        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14 }}>{t('downtime.subtitle')}</p>
      </div>
      <AnalyticsFilterBar filters={filters} onChange={setFilters} />
      {error && <div style={alert}>{error}</div>}
      {loading && !data && <div style={{ color: '#94a3b8', padding: 32, textAlign: 'center' }}>{t('common.loading')}</div>}

      {k && data && (
        <div style={{ opacity: loading ? 0.6 : 1 }}>
          <div style={grid}>
            <Tile label={t('downtime.kpiAvail')} value={fmtHours(k.availMin)} sub={t('downtime.kpiAvailSub')} delta={null} vsLabel="" />
            <Tile label={t('downtime.kpiProd')} value={fmtHours(k.prodMin)} sub={t('downtime.kpiProdSub', { pct: fmtPct(k.availability) })} delta={d(k.prodMin, p?.prodMin)} vsLabel={t('analytics.vsPrev')} />
            <Tile label={t('downtime.kpiLoss')} value={fmtHours(lossMin)} sub={t('downtime.kpiLossSub', { pct: fmtPct(k.availMin > 0 ? lossMin / k.availMin : null) })} delta={d(lossMin, p ? p.ssMin + p.otMin : null)} invert vsLabel={t('analytics.vsPrev')} />
            <Tile label={t('analytics.kpiShortStops')} value={fmtMin(k.ssMin)} sub={t('analytics.kpiShortStopsSub', { count: fmtNum(k.ssCnt), rate: fmtNum(k.stopsPerProdH, 2) })} delta={d(k.ssCnt, p?.ssCnt)} invert vsLabel={t('analytics.vsPrev')} />
            <Tile label={t('downtime.kpiChangeovers')} value={fmtHours(data.changeovers.minutes)} sub={t('downtime.kpiChangeoversSub', { count: String(data.changeovers.count), avg: fmtMin(data.changeovers.avgMinutes), median: fmtMin(data.changeovers.medianMinutes) })} delta={null} vsLabel="" />
            <Tile label={t('downtime.kpiOther')} value={fmtHours(k.otMin)} sub={t('downtime.kpiOtherSub', { count: fmtNum(k.otCnt), pct: fmtPct(k.availMin > 0 ? k.otMin / k.availMin : null) })} delta={d(k.otMin, p?.otMin)} invert vsLabel={t('analytics.vsPrev')} />
            <Tile label={t('analytics.kpiMicroStops')} value={fmtMin(k.microMin)} sub={t('downtime.kpiMicroSub', { count: fmtNum(k.microCnt) })} delta={d(k.microCnt, p?.microCnt)} invert vsLabel={t('analytics.vsPrev')} />
            <Tile label={t('analytics.kpiNoData')} value={fmtHours(k.noDataMin)} sub={t('analytics.kpiNoDataSub')} delta={null} vsLabel="" warn={k.noDataMin > k.availMin * 0.1} />
          </div>

          <div style={card}>
            <div style={cardHead}><b>{t('analytics.timeStructure')}</b><span style={muted}>{t('analytics.timeStructureSub', { hours: fmtNum(k.availMin / 60, 1) })}</span></div>
            <StackedBar format={fmtMin} parts={[
              { label: t('analytics.stProd'), value: k.prodMin, color: '#2f80ed' },
              { label: t('analytics.stShort'), value: k.ssMin, color: '#e8590c' },
              { label: t('analytics.stDown'), value: k.otMin, color: '#6b7280' },
            ]} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16, marginBottom: 16 }}>
            <div style={card}>
              <div style={cardHead}><b>A. {t('analytics.kpiShortStops')}</b><span style={muted}>{t('downtime.shortDef', { min: String(Math.round(data.thresholds.short_stop_seconds / 60)) })}</span></div>
              <div style={{ ...grid, gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 14 }}>
                <Mini label={t('downtime.count')} value={fmtNum(k.ssCnt)} />
                <Mini label={t('downtime.totalTime')} value={fmtMin(k.ssMin)} />
                <Mini label={t('downtime.avgLength')} value={k.ssCnt ? `${fmtNum(k.ssMin / k.ssCnt, 1)} min` : '—'} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{t('downtime.freqByLine')} <span style={muted}>{t('downtime.freqByLineSub')}</span></div>
              <HBar rows={data.byLine.filter(l => l.ssCnt > 0).sort((a, b) => (b.stopsPerProdH ?? 0) - (a.stopsPerProdH ?? 0)).map(l => ({ label: l.line, value: l.stopsPerProdH ?? 0, sub: `${l.ssCnt} · ${fmtMin(l.ssMin)}` }))} format={v => `${fmtNum(v, 2)}/h`} />
            </div>
            <div style={card}>
              <div style={cardHead}><b>B. {t('downtime.kpiChangeovers')}</b><span style={muted}>{t('downtime.chgDef')}</span></div>
              <div style={{ ...grid, gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 14 }}>
                <Mini label={t('downtime.count')} value={fmtNum(data.changeovers.count)} />
                <Mini label={t('downtime.totalTime')} value={fmtMin(data.changeovers.minutes)} />
                <Mini label={t('downtime.avgLength')} value={fmtMin(data.changeovers.avgMinutes)} />
                <Mini label={t('downtime.median')} value={fmtMin(data.changeovers.medianMinutes)} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{t('downtime.transitions')} <span style={muted}>{t('downtime.transitionsSub')}</span></div>
              <HBar rows={data.changeovers.transitions.slice(0, 8).map(tr => ({ label: tr.transition, value: tr.minutes, sub: `${tr.count}× · ${t('downtime.avg')} ${fmtMin(tr.avg)}`, color: '#f2b705' }))} format={fmtMin} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16, marginBottom: 16 }}>
            <div style={card}>
              <div style={cardHead}><b>{t('downtime.byReason')}</b><span style={muted}>{t('downtime.byReasonSub')}</span></div>
              <HBar rows={data.byReason.map(r => ({ label: reasonLabel(r.reason), value: r.minutes, sub: `${r.count}×`, color: '#6b7280' }))} format={fmtMin} />
              {data.manual.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{t('downtime.manual')} <span style={muted}>{t('downtime.manualSub')}</span></div>
                  <HBar rows={data.manual.map(m => ({ label: t(`downtime.manual_${m.type}`), value: m.minutes, sub: `${m.count}×`, color: '#9333ea' }))} format={fmtMin} />
                </div>
              )}
            </div>
            <div style={card}>
              <div style={cardHead}><b>{t('downtime.byLine')}</b><span style={muted}>{t('downtime.byLineSub')}</span></div>
              <HBar rows={[...data.byLine].sort((a, b) => b.downMin - a.downMin).map(l => ({ label: l.line, value: l.downMin, sub: `${l.downCnt}× · Avail. ${fmtPct(l.availability)}`, color: '#6b7280' }))} format={fmtMin} />
            </div>
          </div>

          <div style={card}>
            <div style={cardHead}><b>{t('downtime.longest')}</b><span style={muted}>{t('downtime.longestSub')}</span></div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ background: '#f8f9fa' }}><th style={th}>{t('analytics.colLine')}</th><th style={th}>{t('downtime.colStart')}</th><th style={thR}>{t('downtime.colDuration')}</th><th style={th}>{t('downtime.colReason')}</th><th style={th}>{t('standards.colMaterial')}</th></tr></thead>
              <tbody>
                {data.longest.length === 0 ? <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 20 }}>—</td></tr> : data.longest.map((e, i) => (
                  <tr key={i}><td style={{ ...td, fontWeight: 700 }}>{e.line}</td><td style={td}>{dt(e.start)}</td><td style={{ ...tdR, fontWeight: 700 }}>{fmtMin(e.minutes)}</td><td style={td}>{e.reason ? reasonLabel(e.reason) : '—'}</td><td style={td}>{e.material ?? '—'}</td></tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.changeovers.list.length > 0 && (
            <div style={card}>
              <div style={cardHead}><b>{t('downtime.chgList')}</b><span style={muted}>{t('downtime.chgListSub')}</span></div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: '#f8f9fa' }}><th style={th}>{t('analytics.colLine')}</th><th style={th}>{t('downtime.colStart')}</th><th style={thR}>{t('downtime.colDuration')}</th><th style={th}>{t('downtime.colFrom')}</th><th style={th}>{t('downtime.colTo')}</th></tr></thead>
                <tbody>
                  {data.changeovers.list.map((c, i) => (
                    <tr key={i}><td style={{ ...td, fontWeight: 700 }}>{c.line}</td><td style={td}>{dt(c.start)}</td><td style={{ ...tdR, fontWeight: 700 }}>{fmtMin(c.minutes)}</td>
                      <td style={td}>{c.from_material ?? '?'} {c.from_color ?? ''} <span style={muted}>{c.from_sku}</span></td>
                      <td style={td}>{c.to_material ?? '?'} {c.to_color ?? ''} <span style={muted}>{c.to_sku}</span></td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Mini: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: 0.5, textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
  </div>
);

export default Downtime;
